import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaMobileAlt,
  FaLaptopCode,
  FaGift,
  FaCashRegister,
  FaBrain,
  FaRobot,
  FaServer,
  FaPaintBrush,
  FaShoppingCart,
  FaShieldAlt,
} from 'react-icons/fa';
import {
  SiFlutter,
  SiReact,
  SiNodedotjs,
  SiFirebase,
  SiMongodb,
  SiPostgresql,
  SiDocker,
  SiTypescript,
  SiFigma,
  SiNextdotjs,
  SiAndroid,
  SiDart,
  SiShopify,
  SiWordpress,
  SiGraphql,
  SiPython,
  SiPhp,
  SiLaravel,
  SiMysql,
  SiRedis,
  SiJavascript,
  SiTailwindcss,
  SiVuedotjs,
  SiStripe,
} from 'react-icons/si';
import './TechDivision.css';

const techStack = [
  { icon: <SiJavascript />, label: 'JavaScript', color: '#F7DF1E', group: 'frontend' },
  { icon: <SiReact />,      label: 'React',      color: '#61DAFB', group: 'frontend' },
  { icon: <SiNextdotjs />,  label: 'Next.js',    color: '#ffffff', group: 'frontend' },
  { icon: <SiVuedotjs />,   label: 'Vue.js',     color: '#42B883', group: 'frontend' },
  { icon: <SiTypescript />, label: 'TypeScript', color: '#3178C6', group: 'frontend' },
  { icon: <SiTailwindcss />,label: 'Tailwind',   color: '#38BDF8', group: 'frontend' },
  { icon: <SiFigma />,      label: 'Figma',      color: '#F24E1E', group: 'frontend' },
  { icon: <SiFlutter />,    label: 'Flutter',    color: '#54C5F8', group: 'mobile'   },
  { icon: <SiDart />,       label: 'Dart',       color: '#0175C2', group: 'mobile'   },
  { icon: <SiAndroid />,    label: 'Android',    color: '#3DDC84', group: 'mobile'   },
  { icon: <SiPhp />,        label: 'PHP',        color: '#8892BF', group: 'backend'  },
  { icon: <SiLaravel />,    label: 'Laravel',    color: '#FF2D20', group: 'backend'  },
  { icon: <SiNodedotjs />,  label: 'Node.js',    color: '#68A063', group: 'backend'  },
  { icon: <SiPython />,     label: 'Python',     color: '#3776AB', group: 'backend'  },
  { icon: <SiGraphql />,    label: 'GraphQL',    color: '#E10098', group: 'backend'  },
  { icon: <SiMysql />,      label: 'MySQL',      color: '#4479A1', group: 'database' },
  { icon: <SiPostgresql />, label: 'PostgreSQL', color: '#336791', group: 'database' },
  { icon: <SiMongodb />,    label: 'MongoDB',    color: '#47A248', group: 'database' },
  { icon: <SiRedis />,      label: 'Redis',      color: '#DC382D', group: 'database' },
  { icon: <SiFirebase />,   label: 'Firebase',   color: '#FFA000', group: 'database' },
  { icon: <SiDocker />,     label: 'Docker',     color: '#2496ED', group: 'devops'   },
  { icon: <SiShopify />,    label: 'Shopify',    color: '#96BF48', group: 'platform' },
  { icon: <SiWordpress />,  label: 'WordPress',  color: '#21759B', group: 'platform' },
  { icon: <SiStripe />,     label: 'Stripe',     color: '#635BFF', group: 'platform' },
];

const stackGroups = [
  { key: 'all',      label: 'All' },
  { key: 'frontend', label: 'Frontend' },
  { key: 'mobile',   label: 'Mobile' },
  { key: 'backend',  label: 'Backend' },
  { key: 'database', label: 'Database' },
  { key: 'devops',   label: 'DevOps' },
  { key: 'platform', label: 'Platforms' },
];

const services = [
  {
    slug: 'mobile-apps',
    icon: <FaMobileAlt />,
    title: 'Mobile Apps',
    description: 'We build apps for your phone — Android or iPhone. Your customers can use your service anytime, anywhere.',
    tags: ['React Native', 'Flutter', 'iOS', 'Android'],
  },
  {
    slug: 'web-applications',
    icon: <FaLaptopCode />,
    title: 'Web Applications',
    description: 'Need a website or online system? We build fast, good-looking websites that work perfectly on any device.',
    tags: ['React', 'Node.js', 'Next.js', 'TypeScript'],
  },
  {
    slug: 'loyalty-reward',
    icon: <FaGift />,
    title: 'Loyalty & Reward Systems',
    description: 'Keep your customers coming back. We build digital loyalty card and reward point systems for your business.',
    tags: ['Loyalty Cards', 'Rewards', 'CRM', 'Retention'],
  },
  {
    slug: 'pos-systems',
    icon: <FaCashRegister />,
    title: 'POS Systems',
    description: 'Manage your sales and billing easily. Perfect for shops, restaurants, and any business that needs a cashier system.',
    tags: ['Point of Sale', 'Billing', 'Inventory', 'Reports'],
  },
  {
    slug: 'ai-bots',
    icon: <FaBrain />,
    title: 'AI Applications & Bots',
    description: 'Let technology do the hard work. We build AI chatbots and smart systems that serve your customers automatically, 24/7.',
    tags: ['Chatbots', 'Automation', 'AI', 'Smart Systems'],
  },
  {
    slug: 'custom-software',
    icon: <FaRobot />,
    title: 'Custom Software',
    description: 'Have a unique idea? We build it. Whatever software your business needs, we design and deliver it exactly as you want.',
    tags: ['ERP', 'CRM', 'Any Platform', 'On-demand'],
  },
  {
    slug: 'backend-api',
    icon: <FaServer />,
    title: 'Backend & API Systems',
    description: 'The engine behind your app or website. We build the systems that store your data and keep everything running smoothly.',
    tags: ['Node.js', 'Laravel', 'REST/GraphQL', 'Docker'],
  },
  {
    slug: 'ui-ux-design',
    icon: <FaPaintBrush />,
    title: 'UI/UX Design',
    description: 'We make your app or website look great and feel easy to use — so your customers enjoy every visit.',
    tags: ['Figma', 'Prototyping', 'UI Design', 'UX Research'],
  },
  {
    slug: 'ecommerce',
    icon: <FaShoppingCart />,
    title: 'E-Commerce Solutions',
    description: 'Sell your products online. We build your online shop with secure payments, stock management, and everything you need.',
    tags: ['Shopify', 'WooCommerce', 'Stripe', 'Payments'],
  },
  {
    slug: 'cybersecurity',
    icon: <FaShieldAlt />,
    title: 'Cybersecurity & Pentesting',
    description: 'We check your website or app for security weaknesses, give you a full report, and fix every problem we find.',
    tags: ['Penetration Testing', 'Vulnerability Reports', 'Security Audits', 'Bug Fixes'],
  },
];

const TechDivision = ({ setActivePage }) => {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeStackGroup, setActiveStackGroup] = useState('all');
  const visibleStack = activeStackGroup === 'all' ? techStack : techStack.filter(t => t.group === activeStackGroup);

  return (
    <section className="tech" id="tech" ref={ref}>
      <div className="tech__bg-text">SOFTWARE</div>
      <div className="tech__container">
        <motion.div
          className="tech__header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <span className="section-tag">Software Division</span>
          <h2 className="section-title">
            What We Can
            <br />
            <span className="text-green">Build for You</span>
          </h2>
          <p className="section-description">
            We build software that solves real problems. Tell us what you need —
            we handle the rest, from idea to launch.
          </p>
        </motion.div>

        {/* <motion.div
          className="tech__stats"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
        > */}
          {/* <div className="tech__stat">
            <span className="tech__stat-number">15+</span>
            <span className="tech__stat-label">Happy Customers</span>
          </div>
          <div className="tech__stat-divider" />
          <div className="tech__stat">
            <span className="tech__stat-number">100+</span>
            <span className="tech__stat-label">Successful Projects</span>
          </div>
          <div className="tech__stat-divider" />
          <div className="tech__stat">
            <span className="tech__stat-number">100%</span>
            <span className="tech__stat-label">Client Satisfaction</span>
          </div>
        </motion.div> */}

        <div className="tech__grid">
          {services.map((service, i) => (
            <motion.div
              key={i}
              className="tech__card tech__card--clickable"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => setActivePage && setActivePage(service.slug)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setActivePage && setActivePage(service.slug)}
            >
              <div className="tech__card-glow" />
              <div className="tech__card-icon">{service.icon}</div>
              <h3 className="tech__card-title">{service.title}</h3>
              <p className="tech__card-desc">{service.description}</p>
              <div className="tech__card-tags">
                {service.tags.map((tag, j) => (
                  <span key={j} className="tech__card-tag">{tag}</span>
                ))}
              </div>
              <AnimatePresence>
                {hoveredIndex === i && (
                  <motion.div
                    className="tech__card-shine"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Tech Stack */}
        <motion.div
          className="tech__stack"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="tech__stack-label">Technologies We Work With</p>
          <div className="tech__stack-filters">
            {stackGroups.map(g => (
              <button
                key={g.key}
                className={`tech__stack-filter${activeStackGroup === g.key ? ' active' : ''}`}
                onClick={() => setActiveStackGroup(g.key)}
              >
                {g.label}
              </button>
            ))}
          </div>
          <div className="tech__stack-grid">
            {visibleStack.map((tech, i) => (
              <motion.div
                key={tech.label}
                className="tech__stack-item"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                whileHover={{ scale: 1.15, y: -4 }}
                style={{ '--tech-color': tech.color }}
              >
                <span className="tech__stack-icon">{tech.icon}</span>
                <span className="tech__stack-name">{tech.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="tech__process"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h3 className="tech__process-title">Our Process</h3>
          <div className="tech__process-steps">
            {['Discovery', 'Design', 'Develop', 'Deploy'].map((step, i) => (
              <div className="tech__process-step" key={i}>
                <div className="tech__process-num">{String(i + 1).padStart(2, '0')}</div>
                <span>{step}</span>
                {i < 3 && <div className="tech__process-line" />}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechDivision;
